// pages/Credits.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Search,
  RefreshCw,
  Upload,
  Download,
  TrendingUp,
  Building2,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Eye,
  RotateCcw,
  X,
  AlertCircle,
  Filter,
  FileText,
  Users,
  DollarSign,
  MapPin,
  Briefcase,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

// ─── constants ────────────────────────────────────────────────────────────────

const CREDIT_CATEGORIES = ["Excellent", "Good", "Fair", "Poor", "High Risk"];

const RISK_LEVELS = ["Low", "Medium", "High", "Critical"];

const CATEGORY_COLORS = {
  Excellent: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  Good: "bg-green-500/10 text-green-400 border-green-500/30",
  Fair: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  Poor: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  "High Risk": "bg-red-500/10 text-red-400 border-red-500/30",
};

const CATEGORY_COLORS_LIGHT = {
  Excellent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Good: "bg-green-50 text-green-700 border-green-200",
  Fair: "bg-yellow-50 text-yellow-700 border-yellow-200",
  Poor: "bg-orange-50 text-orange-700 border-orange-200",
  "High Risk": "bg-red-50 text-red-700 border-red-200",
};

const RISK_COLORS = {
  Low: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  Medium: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  High: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  Critical: "bg-red-500/10 text-red-400 border-red-500/30",
};

const RISK_COLORS_LIGHT = {
  Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Medium: "bg-yellow-50 text-yellow-700 border-yellow-200",
  High: "bg-orange-50 text-orange-700 border-orange-200",
  Critical: "bg-red-50 text-red-700 border-red-200",
};

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency(val) {
  if (val === null || val === undefined) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ─── sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, trend, isDarkMode }) {
  return (
    <div
      className={`rounded-xl p-5 border transition-colors ${
        isDarkMode
          ? "bg-[#0C0E12] border-gray-700/50"
          : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <span
          className={`text-[13px] font-medium ${isDarkMode ? "text-[#94979C]" : "text-gray-500"}`}
        >
          {label}
        </span>
        <Icon
          size={16}
          className={isDarkMode ? "text-gray-500" : "text-gray-400"}
        />
      </div>
      <div
        className={`text-2xl font-bold ${isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"}`}
      >
        {value}
      </div>
      {trend !== undefined && (
        <div
          className={`text-xs mt-1 ${
            trend >= 0 ? "text-emerald-500" : "text-red-500"
          }`}
        >
          {trend >= 0 ? "+" : ""}
          {trend} pts
        </div>
      )}
    </div>
  );
}

function CategoryBadge({ category, isDarkMode }) {
  const cls = isDarkMode
    ? CATEGORY_COLORS[category] || CATEGORY_COLORS["High Risk"]
    : CATEGORY_COLORS_LIGHT[category] || CATEGORY_COLORS_LIGHT["High Risk"];
  if (!category) return <span className="text-gray-400">—</span>;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-medium ${cls}`}
    >
      {category}
    </span>
  );
}

function RiskBadge({ risk, isDarkMode }) {
  const cls = isDarkMode
    ? RISK_COLORS[risk] || RISK_COLORS.Critical
    : RISK_COLORS_LIGHT[risk] || RISK_COLORS_LIGHT.Critical;
  if (!risk) return <span className="text-gray-400">—</span>;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-medium ${cls}`}
    >
      {risk}
    </span>
  );
}

function CreditScoreRing({ score, size = 48, isDarkMode }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(100, Math.max(0, score || 0)) / 100;
  const offset = circumference * (1 - progress);

  const getColor = () => {
    if (score >= 80) return "#10b981"; // emerald
    if (score >= 60) return "#22c55e"; // green
    if (score >= 40) return "#eab308"; // yellow
    if (score >= 20) return "#f97316"; // orange
    return "#ef4444"; // red
  };

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={isDarkMode ? "#1f2937" : "#e5e7eb"}
          strokeWidth="4"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute text-xs font-bold">
        {score !== null && score !== undefined ? Math.round(score) : "—"}
      </span>
    </div>
  );
}

// ─── Credit Detail Modal ──────────────────────────────────────────────────────

function CreditDetailModal({ isDarkMode, credit, onClose }) {
  if (!credit) return null;

  const InfoRow = ({ label, value, highlight }) => (
    <div className="flex items-start justify-between py-2 border-b border-gray-700/20">
      <span
        className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
      >
        {label}
      </span>
      <span
        className={`text-sm text-right ${
          highlight
            ? "font-semibold text-blue-400"
            : isDarkMode
              ? "text-gray-300"
              : "text-gray-700"
        }`}
      >
        {value || "—"}
      </span>
    </div>
  );

  const Section = ({ title, children }) => (
    <div className="mb-6">
      <h3
        className={`text-sm font-semibold mb-3 pb-1 border-b ${
          isDarkMode
            ? "text-gray-300 border-gray-700"
            : "text-gray-700 border-gray-200"
        }`}
      >
        {title}
      </h3>
      {children}
    </div>
  );

  const overlay = "fixed inset-0 z-50 flex items-center justify-center p-4";
  const backdrop = "absolute inset-0 bg-black/60 backdrop-blur-sm";
  const panel = `relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
    isDarkMode
      ? "bg-[#0A0B0F] border border-gray-700/50"
      : "bg-white border border-gray-200"
  }`;

  return (
    <div className={overlay}>
      <div className={backdrop} onClick={onClose} />
      <div className={panel}>
        {/* Header */}
        <div
          className={`sticky top-0 flex items-center justify-between p-6 border-b ${
            isDarkMode
              ? "border-gray-700/50 bg-[#0A0B0F]"
              : "border-gray-200 bg-white"
          }`}
        >
          <div>
            <h2
              className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {credit.companyName || credit.companyReg}
            </h2>
            <p
              className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              Company Registration: {credit.companyReg}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-700/40 transition-colors"
          >
            <X
              size={18}
              className={isDarkMode ? "text-gray-400" : "text-gray-500"}
            />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Credit Score Summary */}
          <div
            className={`flex items-center gap-6 p-4 rounded-xl ${
              isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"
            }`}
          >
            <CreditScoreRing
              score={credit.creditScore}
              size={64}
              isDarkMode={isDarkMode}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <CategoryBadge
                  category={credit.creditRatingCategory}
                  isDarkMode={isDarkMode}
                />
                <RiskBadge risk={credit.riskLevel} isDarkMode={isDarkMode} />
              </div>
              <p
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Based on credit rating ({credit.latestCreditRating || "N/A"})
                and financial indicators
              </p>
            </div>
          </div>

          {/* Credit Limits */}
          <Section title="Credit Limits">
            <InfoRow
              label="Latest Credit Limit"
              value={fmtCurrency(credit.latestCreditLimit)}
            />
            <InfoRow
              label="Contract Limit"
              value={fmtCurrency(credit.contractLimit)}
            />
            <InfoRow
              label="Recommended Credit Limit"
              value={fmtCurrency(credit.recommendedCreditLimit)}
              highlight
            />
          </Section>

          {/* Company Information */}
          <Section title="Company Information">
            <InfoRow label="Company Name" value={credit.companyName} />
            <InfoRow label="Registration Number" value={credit.companyReg} />
            <InfoRow
              label="Incorporation Date"
              value={fmtDate(credit.incorporationDate)}
            />
            <InfoRow label="Company Type" value={credit.companyType} />
            <InfoRow label="SIC Code" value={credit.sicCode07} />
            <InfoRow label="SIC Description" value={credit.sicDescription} />
            <InfoRow label="Website" value={credit.website} />
            <InfoRow label="Telephone" value={credit.telephone} />
          </Section>

          {/* Address */}
          <Section title="Address">
            <InfoRow label="Address" value={credit.address1} />
            {credit.address2 && <InfoRow label="" value={credit.address2} />}
            {credit.address3 && <InfoRow label="" value={credit.address3} />}
            <InfoRow label="Postcode" value={credit.postcode} />
          </Section>

          {/* Directors */}
          {credit.directors && credit.directors.length > 0 && (
            <Section title={`Directors (${credit.directorsCount})`}>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {credit.directors.map((director, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg ${
                      isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"
                    }`}
                  >
                    <p
                      className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {director.forename} {director.surname}
                    </p>
                    {director.occupation && (
                      <p
                        className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                      >
                        {director.occupation}
                      </p>
                    )}
                    {director.birthDate && (
                      <p
                        className={`text-xs ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
                      >
                        Born: {fmtDate(director.birthDate)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Import Metadata */}
          <Section title="Import Information">
            <InfoRow
              label="Last Imported"
              value={fmtDate(credit.lastImportedAt)}
            />
            <InfoRow label="Import Source" value={credit.importSource} />
            <InfoRow label="Import Batch" value={credit.importBatchId} />
          </Section>
        </div>

        {/* Footer */}
        <div
          className={`sticky bottom-0 flex items-center justify-end gap-3 p-6 border-t ${
            isDarkMode
              ? "border-gray-700/50 bg-[#0A0B0F]"
              : "border-gray-200 bg-white"
          }`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              isDarkMode
                ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Import Modal ─────────────────────────────────────────────────────────────

function ImportModal({ isDarkMode, onClose, onDone }) {
  const [files, setFiles] = useState({
    coreFile: null,
    creditFile: null,
    directorFile: null,
  });
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const coreRef = useRef(null);
  const creditRef = useRef(null);
  const directorRef = useRef(null);
  const fileRefs = {
    coreFile: coreRef,
    creditFile: creditRef,
    directorFile: directorRef,
  };

  const handleFileChange = (type, file) => {
    setFiles((prev) => ({ ...prev, [type]: file }));
  };

  const handleImport = async () => {
    if (!files.coreFile && !files.creditFile && !files.directorFile) {
      setError("Please select at least one file to import");
      return;
    }

    setImporting(true);
    setError(null);
    try {
      const res = await apiService.importCredits(files);
      setResult(res);
      onDone();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const renderFileSelector = (type, label, description) => (
    <div
      onClick={() => fileRefs[type].current?.click()}
      className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
        files[type]
          ? isDarkMode
            ? "border-emerald-500/50 bg-emerald-500/5"
            : "border-emerald-400 bg-emerald-50"
          : isDarkMode
            ? "border-gray-700 hover:border-blue-500/50"
            : "border-gray-300 hover:border-blue-400"
      }`}
    >
      <Upload size={20} className="mx-auto mb-1" />
      <p className="text-sm font-medium">
        {files[type] ? files[type].name : label}
      </p>
      <p
        className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
      >
        {description}
      </p>
      {files[type] && (
        <p className="text-xs text-emerald-500 mt-1">
          {(files[type].size / 1024).toFixed(0)} KB
        </p>
      )}
    </div>
  );

  const overlay = "fixed inset-0 z-50 flex items-center justify-center p-4";
  const backdrop = "absolute inset-0 bg-black/60 backdrop-blur-sm";
  const panel = `relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
    isDarkMode
      ? "bg-[#0A0B0F] border border-gray-700/50"
      : "bg-white border border-gray-200"
  }`;

  return (
    <div className={overlay}>
      <div className={backdrop} onClick={onClose} />
      <div className={panel}>
        <div
          className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700/50" : "border-gray-200"}`}
        >
          <h2
            className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Import Credit Data
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-700/40 transition-colors"
          >
            <X
              size={18}
              className={isDarkMode ? "text-gray-400" : "text-gray-500"}
            />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!result ? (
            <>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Upload one or more of the following CSV files (pipe-delimited):
              </p>

              <div className="space-y-3">
                {renderFileSelector(
                  "coreFile",
                  "Core Base File",
                  "Company registration & basic info",
                )}
                {renderFileSelector(
                  "creditFile",
                  "Credit Rating File",
                  "Credit ratings and limits",
                )}
                {renderFileSelector(
                  "directorFile",
                  "Director File",
                  "Company directors information",
                )}
              </div>

              <input
                ref={fileRefs.coreFile}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) =>
                  handleFileChange("coreFile", e.target.files[0] || null)
                }
              />
              <input
                ref={fileRefs.creditFile}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) =>
                  handleFileChange("creditFile", e.target.files[0] || null)
                }
              />
              <input
                ref={fileRefs.directorFile}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) =>
                  handleFileChange("directorFile", e.target.files[0] || null)
                }
              />

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  <AlertCircle size={14} />
                  {error}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm">
                <CheckCircle size={14} />
                Import completed successfully
              </div>

              <div
                className={`rounded-lg p-4 border ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
              >
                <h4 className="text-sm font-semibold mb-3">Import Summary</h4>
                <div className="space-y-2 text-sm">
                  {result.results?.core && (
                    <div className="flex justify-between">
                      <span>Core File:</span>
                      <span>
                        {result.results.core.created} created,{" "}
                        {result.results.core.updated} updated
                      </span>
                    </div>
                  )}
                  {result.results?.credit && (
                    <div className="flex justify-between">
                      <span>Credit File:</span>
                      <span>
                        {result.results.credit.processed} processed,
                        {result.results.credit.updated} updated
                      </span>
                    </div>
                  )}
                  {result.results?.director && (
                    <div className="flex justify-between">
                      <span>Director File:</span>
                      <span>
                        {result.results.director.updated} companies updated
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-700/30">
                    <span className="font-medium">Calculated Scores:</span>
                    <span>{result.results?.calculated || 0} companies</span>
                  </div>
                </div>
              </div>

              {(result.results?.core?.errors?.length > 0 ||
                result.results?.credit?.errors?.length > 0) && (
                <div
                  className={`rounded-lg p-3 border text-xs max-h-32 overflow-y-auto ${
                    isDarkMode
                      ? "bg-red-950/20 border-red-800/30 text-red-400"
                      : "bg-red-50 border-red-200 text-red-700"
                  }`}
                >
                  {result.results.core?.errors?.map((e, i) => (
                    <div key={`core-${i}`}>
                      Core Row {e.row}: {e.error}
                    </div>
                  ))}
                  {result.results.credit?.errors?.map((e, i) => (
                    <div key={`credit-${i}`}>
                      Credit Row {e.row}: {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className={`flex items-center justify-end gap-3 p-6 border-t ${isDarkMode ? "border-gray-700/50" : "border-gray-200"}`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              isDarkMode
                ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {result ? "Close" : "Cancel"}
          </button>
          {!result && (
            <button
              onClick={handleImport}
              disabled={importing}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {importing ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <Upload size={14} />
              )}
              {importing ? "Importing..." : "Import"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Credits Page ────────────────────────────────────────────────────────

const Credits = () => {
  const { isDarkMode } = useTheme();

  const [credits, setCredits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    ratingCategory: "",
    riskLevel: "",
    minRating: "",
    maxRating: "",
    sicCode: "",
    postcode: "",
  });
  const [sortBy, setSortBy] = useState("companyName");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [showFilters, setShowFilters] = useState(false);

  const fetchCredits = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: pagination.limit,
          sortBy,
          sortOrder,
          ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
        };
        const res = await apiService.getCredits(params);
        setCredits(res.data || []);
        setPagination((p) => ({ ...p, ...res.pagination, page }));
      } catch (err) {
        console.error("Failed to fetch credits:", err);
      } finally {
        setLoading(false);
      }
    },
    [filters, sortBy, sortOrder, pagination.limit],
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiService.getCreditStats();
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  }, []);

  useEffect(() => {
    fetchCredits(1);
  }, [filters, sortBy, sortOrder]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(col);
      setSortOrder("ASC");
    }
  };

  const handleRecalculate = async (companyReg) => {
    try {
      await apiService.recalculateCredit(companyReg);
      fetchCredits(pagination.page);
      fetchStats();
    } catch (err) {
      console.error("Failed to recalculate:", err);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await apiService.exportCredits();
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `credits_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronDown size={12} className="opacity-30" />;
    return sortOrder === "ASC" ? (
      <ChevronUp size={12} />
    ) : (
      <ChevronDown size={12} />
    );
  };

  const th = `py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none ${
    isDarkMode ? "text-gray-500" : "text-gray-500"
  }`;

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${
        isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"
      }`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1
              className={`text-2xl sm:text-3xl font-bold mb-1 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Credit Hub
            </h1>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Monitor company credit ratings, limits, and risk assessments
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => fetchCredits(pagination.page)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-800"
                  : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-800"
                  : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Download size={14} />
              {exporting ? "Exporting..." : "Export"}
            </button>
            <button
              onClick={() => setShowImport(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              <Upload size={14} />
              Import Data
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Total Companies"
              value={stats.total}
              icon={Building2}
              isDarkMode={isDarkMode}
            />
            <StatCard
              label="Avg Credit Score"
              value={stats.avgCreditScore}
              icon={TrendingUp}
              trend={stats.avgCreditScoreChange}
              isDarkMode={isDarkMode}
            />
            <StatCard
              label="Total Credit Limit"
              value={fmtCurrency(stats.totalCreditLimit)}
              icon={DollarSign}
              isDarkMode={isDarkMode}
            />
            <StatCard
              label="High Risk"
              value={stats.riskDistribution?.High || 0}
              icon={AlertTriangle}
              isDarkMode={isDarkMode}
            />
          </div>
        )}

        {/* Category & Risk Distribution */}
        {stats && (stats.categoryDistribution || stats.riskDistribution) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Category Distribution */}
            <div
              className={`rounded-xl p-4 border ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700/50"
                  : "bg-white border-gray-200"
              }`}
            >
              <h3
                className={`text-sm font-semibold mb-3 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Credit Rating Distribution
              </h3>
              <div className="space-y-2">
                {CREDIT_CATEGORIES.map((cat) => (
                  <div key={cat} className="flex items-center gap-3">
                    <div className="w-24">
                      <CategoryBadge category={cat} isDarkMode={isDarkMode} />
                    </div>
                    <div className="flex-1 h-2 rounded-full bg-gray-700/30 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-red-500"
                        style={{
                          width: `${
                            ((stats.categoryDistribution?.[cat] || 0) /
                              stats.total) *
                            100
                          }%`,
                        }}
                      />
                    </div>
                    <span
                      className={`text-xs w-12 text-right ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {stats.categoryDistribution?.[cat] || 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Distribution */}
            <div
              className={`rounded-xl p-4 border ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700/50"
                  : "bg-white border-gray-200"
              }`}
            >
              <h3
                className={`text-sm font-semibold mb-3 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Risk Level Distribution
              </h3>
              <div className="space-y-2">
                {RISK_LEVELS.map((risk) => (
                  <div key={risk} className="flex items-center gap-3">
                    <div className="w-16">
                      <RiskBadge risk={risk} isDarkMode={isDarkMode} />
                    </div>
                    <div className="flex-1 h-2 rounded-full bg-gray-700/30 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${
                            ((stats.riskDistribution?.[risk] || 0) /
                              stats.total) *
                            100
                          }%`,
                          backgroundColor:
                            risk === "Low"
                              ? "#10b981"
                              : risk === "Medium"
                                ? "#eab308"
                                : risk === "High"
                                  ? "#f97316"
                                  : "#ef4444",
                        }}
                      />
                    </div>
                    <span
                      className={`text-xs w-12 text-right ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {stats.riskDistribution?.[risk] || 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Search + filter bar */}
        <div
          className={`rounded-xl border p-4 mb-4 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700/50"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search
                size={14}
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              />
              <input
                type="text"
                placeholder="Search by company name, reg number, or SIC description..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, search: e.target.value }))
                }
                className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors ${
                  isDarkMode
                    ? "bg-[#0A0B0F] border-gray-700 text-white placeholder-gray-600"
                    : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                }`}
              />
            </div>

            <select
              value={filters.ratingCategory}
              onChange={(e) =>
                setFilters((p) => ({ ...p, ratingCategory: e.target.value }))
              }
              className={`px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors ${
                isDarkMode
                  ? "bg-[#0A0B0F] border-gray-700 text-white"
                  : "bg-gray-50 border-gray-200 text-gray-900"
              }`}
            >
              <option value="">All Categories</option>
              {CREDIT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              value={filters.riskLevel}
              onChange={(e) =>
                setFilters((p) => ({ ...p, riskLevel: e.target.value }))
              }
              className={`px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors ${
                isDarkMode
                  ? "bg-[#0A0B0F] border-gray-700 text-white"
                  : "bg-gray-50 border-gray-200 text-gray-900"
              }`}
            >
              <option value="">All Risk Levels</option>
              {RISK_LEVELS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>

            <button
              onClick={() => setShowFilters((p) => !p)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters
                  ? "bg-blue-600 border-blue-600 text-white"
                  : isDarkMode
                    ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Filter size={14} />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-700/30">
              <div>
                <label
                  className={`block text-xs mb-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Min Credit Score
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={filters.minRating}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, minRating: e.target.value }))
                  }
                  className={`w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/50 ${
                    isDarkMode
                      ? "bg-[#0A0B0F] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  placeholder="0"
                />
              </div>
              <div>
                <label
                  className={`block text-xs mb-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Max Credit Score
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={filters.maxRating}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, maxRating: e.target.value }))
                  }
                  className={`w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/50 ${
                    isDarkMode
                      ? "bg-[#0A0B0F] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  placeholder="100"
                />
              </div>
              <div>
                <label
                  className={`block text-xs mb-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  SIC Code
                </label>
                <input
                  type="text"
                  value={filters.sicCode}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, sicCode: e.target.value }))
                  }
                  className={`w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/50 ${
                    isDarkMode
                      ? "bg-[#0A0B0F] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  placeholder="e.g., 62020"
                />
              </div>
              <div>
                <label
                  className={`block text-xs mb-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Postcode Area
                </label>
                <input
                  type="text"
                  value={filters.postcode}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, postcode: e.target.value }))
                  }
                  className={`w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/50 ${
                    isDarkMode
                      ? "bg-[#0A0B0F] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  placeholder="e.g., SW1A"
                />
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div
          className={`rounded-xl border overflow-hidden ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700/50"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead
                className={`border-b ${
                  isDarkMode
                    ? "border-gray-700/50 bg-[#0A0B0F]"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <tr>
                  {[
                    { label: "Company", col: "companyName" },
                    { label: "Reg No.", col: "companyReg" },
                    { label: "Credit Score", col: "creditScore" },
                    { label: "Rating", col: "creditRatingCategory" },
                    { label: "Risk", col: "riskLevel" },
                    { label: "Credit Limit", col: "latestCreditLimit" },
                    { label: "SIC", col: "sicCode07" },
                    { label: "Postcode", col: "postcode" },
                    { label: "", col: null },
                  ].map(({ label, col }) => (
                    <th
                      key={label}
                      className={th}
                      onClick={col ? () => handleSort(col) : undefined}
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {col && <SortIcon col={col} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr
                      key={i}
                      className={`border-b ${
                        isDarkMode ? "border-gray-700/30" : "border-gray-100"
                      }`}
                    >
                      {Array.from({ length: 9 }).map((__, j) => (
                        <td key={j} className="py-4 px-4">
                          <div
                            className={`h-4 rounded ${
                              isDarkMode ? "bg-gray-800" : "bg-gray-200"
                            } animate-pulse`}
                            style={{ width: `${60 + Math.random() * 40}%` }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : credits.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center">
                      <Building2
                        size={32}
                        className={`mx-auto mb-3 ${
                          isDarkMode ? "text-gray-700" : "text-gray-300"
                        }`}
                      />
                      <p
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }
                      >
                        No credit records found
                      </p>
                    </td>
                  </tr>
                ) : (
                  credits.map((credit) => (
                    <tr
                      key={credit.id}
                      className={`border-b transition-colors cursor-pointer ${
                        isDarkMode
                          ? "border-gray-700/30 hover:bg-gray-800/30"
                          : "border-gray-100 hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedCredit(credit)}
                    >
                      <td className="py-3 px-4">
                        <p
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {credit.companyName || "—"}
                        </p>
                      </td>
                      <td
                        className={`py-3 px-4 text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {credit.companyReg}
                      </td>
                      <td className="py-3 px-4">
                        <CreditScoreRing
                          score={credit.creditScore}
                          size={36}
                          isDarkMode={isDarkMode}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <CategoryBadge
                          category={credit.creditRatingCategory}
                          isDarkMode={isDarkMode}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <RiskBadge
                          risk={credit.riskLevel}
                          isDarkMode={isDarkMode}
                        />
                      </td>
                      <td
                        className={`py-3 px-4 text-sm ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {fmtCurrency(credit.latestCreditLimit)}
                      </td>
                      <td
                        className={`py-3 px-4 text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {credit.sicCode07 || "—"}
                      </td>
                      <td
                        className={`py-3 px-4 text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {credit.postcode || "—"}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCredit(credit);
                            }}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isDarkMode
                                ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                                : "hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                            }`}
                          >
                            <Eye size={13} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRecalculate(credit.companyReg);
                            }}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isDarkMode
                                ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                                : "hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                            }`}
                            title="Recalculate credit score"
                          >
                            <RotateCcw size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div
              className={`flex items-center justify-between px-4 py-3 border-t ${
                isDarkMode
                  ? "border-gray-700/50 bg-[#0A0B0F]"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <p
                className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                of {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchCredits(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className={`px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 transition-colors ${
                    isDarkMode
                      ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                      : "border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchCredits(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className={`px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 transition-colors ${
                    isDarkMode
                      ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                      : "border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {selectedCredit && (
        <CreditDetailModal
          isDarkMode={isDarkMode}
          credit={selectedCredit}
          onClose={() => setSelectedCredit(null)}
        />
      )}

      {showImport && (
        <ImportModal
          isDarkMode={isDarkMode}
          onClose={() => setShowImport(false)}
          onDone={() => {
            fetchCredits(1);
            fetchStats();
          }}
        />
      )}
    </div>
  );
};

export default Credits;
